import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MicrosoftIdentityService from '~/modules/services/microsoft-identity-service';
import SsoButton from '../sso-button';

/**
 * Microsoft's four-square logo, in their published colours and proportions. Inlined for the
 * same reason as Google's: it is a mark we may use but not redraw, and a login screen should
 * not wait on a network request to show a button.
 */
const MicrosoftMark = () => (
	<svg className="px-auth-modal-sso-mark" viewBox="0 0 23 23" aria-hidden="true" focusable="false">
		<path fill="#f25022" d="M1 1h10v10H1z"/>
		<path fill="#7fba00" d="M12 1h10v10H12z"/>
		<path fill="#00a4ef" d="M1 12h10v10H1z"/>
		<path fill="#ffb900" d="M12 12h10v10H12z"/>
	</svg>
);

/**
 * "Microsoft" for the login modal.
 *
 * Clicking opens Microsoft's account chooser in a popup. The popup returns to `/auth/microsoft`
 * — Microsoft has no `postmessage` shortcut — which posts the authorization code back here, and
 * the API redeems it with the client secret. Renders nothing when no client ID is configured,
 * which is also what a deployment without the Entra app registration looks like.
 *
 * @param {Function} [onBusyChange] - told when a popup opens and when it settles, so the
 *   modal can hold every other login control shut while one is open
 * @param {Function} onCode - receives `{ code, redirectUri, codeVerifier }` to exchange for a
 *   session. All three matter: OAuth requires the same redirect URI to be quoted when the
 *   code is redeemed, and Microsoft will only redeem a challenged code paired with its
 *   PKCE verifier
 * @param {boolean} [disabled] - true while a sign-in is already in flight
 */
const MicrosoftSignInButton = ({ onCode, disabled, onBusyChange }) => {
	const [ready, setReady] = useState(false);
	const [waiting, setWaiting] = useState(false);

	useEffect(() => {
		let cancelled = false;

		// Resolved on mount so the click that opens the popup is still inside the browser's
		// user-activation window. The value arrives with the boot `system-check` request, which
		// is why the button appears a moment after the modal rather than with it.
		MicrosoftIdentityService.clientId()
			.then((clientId) => {
				if(!cancelled && clientId) {
					setReady(true);
				}
			})
			.catch(() => { /* not configured: offer nothing */ });

		return () => {
			cancelled = true;
		};
	}, []);

	const handleClick = async() => {
		setWaiting(true);
		onBusyChange?.(true);

		try {
			onCode(await MicrosoftIdentityService.requestCode());
		}
		catch(e) {
			// Closing the popup is a decision, not a failure. A blocked popup and an
			// unregistered redirect URI land here too, and those are worth a word in the
			// console because they are otherwise indistinguishable from a cancel.
			console.warn(
				`Microsoft sign-in did not complete (${e?.message}). If this was not a cancelled `
				+ `popup, check that ${window.location.origin}/auth/microsoft is a registered `
				+ 'redirect URI on the Entra app registration and in MICROSOFT_SSO_REDIRECT_URIS.'
			);
		}
		finally {
			setWaiting(false);
			onBusyChange?.(false);
		}
	};

	if(!ready) {
		return null;
	}

	return (
		<SsoButton
			name="microsoft"
			label="Microsoft"
			mark={<MicrosoftMark/>}
			onClick={handleClick}
			disabled={disabled || waiting}
		/>
	);
};

MicrosoftSignInButton.propTypes = {
	onCode: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	onBusyChange: PropTypes.func
};

export default MicrosoftSignInButton;
